<script>
    import { createEventDispatcher, onDestroy } from 'svelte';
    import { Rive, Layout, Fit, Alignment, EventType } from '@rive-app/canvas';

    import {
        RIVE_ANIMATIONS,
        changeValueOfAnimationText,
        getAnimationParams,
        getPrefixText
    } from '@src/utils/animations.js';
    import { EventEmitterInstance } from '@root/src/utils/event-emitter';
    import { allowToBet } from '@root/src/store';
    import Backdrop from '../backdrop.svelte';
    import imgBallSpin from '@src/assets/img/sector_ball_spin.webp';
    import { playAudio } from '@root/src/utils/audio';

    export let arrMultipliersLeft;
    export let arrMultipliersRight;

    const stateMachineName = 'State Machine 1';
    const artboardName = 'crazy_ballspin';
    const spinTrigger = 'spin';
    const bonusTrigger = 'bonus';
    const winRightBoolean = 'win_right';

    const indexColorLeft = 'colors_start_left';
    const indexColorRight = 'colors_start_right';
    const indexColorEnd = 'colors_end';

    const customDPR = 1;
    const dispatch = createEventDispatcher();

    let riveInstance;
    let animationParams;
    let sound;
    let timer;
    let timer2;
    let timerAllowBet;

    let flag = false;

    const setTextRun = (arr, side) => {
        arr?.forEach((multiplier, index) => {
            const idx = ++index;
            const prefixText = getPrefixText(multiplier);

            changeValueOfAnimationText(riveInstance.artboard, `text_fill_${side}`, `${prefixText}${multiplier}`);
            changeValueOfAnimationText(riveInstance.artboard, `text_stroke_${side}`, `${prefixText}${multiplier}`);

            changeValueOfAnimationText(riveInstance.artboard, `text_${idx}_fill_${side}`, `${prefixText}${multiplier}`);
            changeValueOfAnimationText(
                riveInstance.artboard,
                `text_${idx}_stroke_${side}`,
                `${prefixText}${multiplier}`
            );
        });
    };

    const runAnimation = canvas => {
        if (riveInstance) {
            riveInstance.cleanup();
        }

        riveInstance = new Rive({
            src: RIVE_ANIMATIONS.BALL_SPIN,
            stateMachines: stateMachineName,
            canvas: canvas,
            autoplay: true,
            artboard: artboardName,
            shouldDisableRiveListeners: false,
            layout: new Layout({
                fit: Fit.Cover,
                alignment: Alignment.Center
            }),
            onLoad: () => {
                riveInstance.resizeDrawingSurfaceToCanvas(customDPR);
                animationParams = getAnimationParams(riveInstance, stateMachineName, [
                    spinTrigger,
                    bonusTrigger,
                    winRightBoolean,
                    indexColorLeft,
                    indexColorRight,
                    indexColorEnd
                ]);

                allowToBet.set(false);

                if (riveInstance) {
                    EventEmitterInstance.on('top-slot-bonusTrigger', () => {
                        timer = setTimeout(() => {
                            animationParams[bonusTrigger].fire();
                        }, 700);
                    });
                }

                setTextRun(arrMultipliersLeft, 'l');
                setTextRun(arrMultipliersRight, 'r');

                EventEmitterInstance.on('response-bonus-ball-spin', response => {
                    allowToBet.set(false);
                    const data = response?.resp?.ball_spin_result;

                    if (data.side === 'RIGHT') {
                        animationParams[winRightBoolean].value = true;
                        animationParams[indexColorEnd].value = 4;
                    }

                    animationParams[spinTrigger].fire();

                    changeValueOfAnimationText(riveInstance.artboard, `text_fill_l`, `×${data?.multiplier_left}`);
                    changeValueOfAnimationText(riveInstance.artboard, `text_stroke_l`, `×${data?.multiplier_left}`);

                    changeValueOfAnimationText(riveInstance.artboard, `text_fill_r`, `×${data?.multiplier_right}`);
                    changeValueOfAnimationText(riveInstance.artboard, `text_stroke_r`, `×${data?.multiplier_right}`);
                });
            },
            onStateChange: event => {
                const eventName = event?.data?.[0];

                if (eventName === 'end_l' || eventName === 'end_r') {
                    EventEmitterInstance.emit('promise-end');
                }
            }
        });

        riveInstance.on(EventType.RiveEvent, event => {
            if (event.data?.name === '' && !flag) {
                timerAllowBet = setTimeout(() => {
                    flag = true;
                    allowToBet.set(true);
                }, 1000);
            }

            if (event.data?.name === 'mult_win_r') {
                timer = setTimeout(() => {
                    animationParams[spinTrigger].fire();
                    playAudio('audio_spin_ball_spin');
                }, 2000);

                timer2 = setTimeout(() => {
                    playAudio('audio_spin_ball_mults');
                }, 2500);
            }
        });
    };

    onDestroy(() => {
        EventEmitterInstance.removeListener('top-slot-bonusTrigger');
        EventEmitterInstance.removeListener('response-bonus-ball-spin');
        riveInstance?.cleanup();
        sound?.unload();
        clearTimeout(timer);
        clearTimeout(timer2);
        clearTimeout(timerAllowBet);
    });
</script>

<div class="fixed inset-0">
    <Backdrop />
</div>
<div class="absolute inset-0 z-9000 mx-auto h-full w-full">
    <img src={imgBallSpin} class="absolute inset-0 top-8 mx-auto w-32 tablet:w-44" alt="ball spin img" />
    <canvas class="h-full w-full" width="500" height="570" use:runAnimation />
</div>
